import type { V1Step } from '@/generated/hooks/jeeves/models';
import type { IFunctionCallEvent } from '@/modules/automation-run/hooks/useRunJournalEvents';

export type TStepRunStatus =
  | 'done'
  | 'running'
  | 'paused'
  | 'failed'
  | 'pending';

interface IDeriveStepRunStatusesParams {
  steps: V1Step[];
  events: IFunctionCallEvent[];
  isRunTerminal: boolean;
  isRunFailed: boolean;
  /** True while the run is paused in `AwaitingGuidanceRunState` — the active step is 'paused', not 'running'. */
  isRunAwaitingGuidance?: boolean;
  /**
   * 1-based line number from `RunState.failed.location` — the actual source
   * position of the error. Used to identify which step failed by containment
   * in the step's `entry_function.location` range, rather than guessing "the
   * step after the last completed event" (wrong for automations that skip
   * steps conditionally).
   */
  failedLine?: number;
  /**
   * Same idea as `failedLine`, but from `RunState.awaiting_guidance.location`.
   * `awaiting_guidance` is itself always the result of a runtime exception
   * (see the manifest SOP view design doc) — during a test run there's no
   * Astral subagent to auto-patch it, so it's the primary "this step hit an
   * error" signal, not a benign pause.
   */
  awaitingGuidanceLine?: number;
  /**
   * True when the journal produced no events for this run at all.
   *
   * Some events means the journal is working, so steps lacking one of their
   * own were legitimately skipped (conditionals) and a terminal-success run can
   * mark them done. Zero events means it told us nothing — whether the fetch
   * failed or returned `{ run_events: [] }` with a 200 — and inferring success
   * for every step from the run object alone paints the whole run green on no
   * evidence at all.
   */
  eventsUnavailable?: boolean;
}

/** Finds the step whose `entry_function.location` range contains `line` (a
 *  1-based source line from `RunState.failed`/`awaiting_guidance.location`).
 *  Returns -1 if no step's range brackets it. */
function locateStepIndexByLine(steps: V1Step[], line: number): number {
  return steps.findIndex(step => {
    const start = step.entry_function?.location?.start_position?.line;
    const end = step.entry_function?.location?.end_position?.line;
    return start != null && end != null && line >= start && line < end;
  });
}

/**
 * Matches a run's function-call journal events to manifest steps by entry
 * function name (in step order, same idea `RunPage`/`useRunJournalEvents`
 * already use for real runs) to find how many steps have completed.
 *
 * A failed or awaiting-guidance run's step is identified from
 * `RunState.failed.location`/`awaiting_guidance.location` when available (the
 * step whose entry-function body contains that source line), falling back to
 * "the step after the last completed event" otherwise.
 */
export function deriveStepRunStatuses({
  steps,
  events,
  isRunTerminal,
  isRunFailed,
  isRunAwaitingGuidance = false,
  failedLine,
  awaitingGuidanceLine,
  eventsUnavailable = false,
}: IDeriveStepRunStatusesParams): Map<number, TStepRunStatus> {
  const completedFunctionNames = new Set(
    events.map(event => event.functionName).filter(Boolean),
  );
  const statuses = new Map<number, TStepRunStatus>();

  let completedCount = 0;
  for (const step of steps) {
    const fn = step.entry_function?.name;
    if (fn && completedFunctionNames.has(fn)) completedCount += 1;
  }

  let failedIndex: number | undefined;
  if (isRunFailed && failedLine != null) {
    const locatedIndex = locateStepIndexByLine(steps, failedLine);
    if (locatedIndex !== -1) failedIndex = locatedIndex;
  }
  if (isRunFailed && failedIndex === undefined) failedIndex = completedCount;

  let awaitingGuidanceIndex: number | undefined;
  if (isRunAwaitingGuidance && awaitingGuidanceLine != null) {
    const locatedIndex = locateStepIndexByLine(steps, awaitingGuidanceLine);
    if (locatedIndex !== -1) awaitingGuidanceIndex = locatedIndex;
  }
  if (isRunAwaitingGuidance && awaitingGuidanceIndex === undefined) {
    awaitingGuidanceIndex = completedCount;
  }

  steps.forEach((step, index) => {
    if (step.number == null) return;
    if (isRunFailed) {
      if (index < (failedIndex as number)) {
        statuses.set(step.number, 'done');
      } else if (index === failedIndex) {
        statuses.set(step.number, 'failed');
      } else {
        statuses.set(step.number, 'pending');
      }
      return;
    }
    if (isRunTerminal) {
      // A terminal-success run marks every step done, including ones with no
      // event of their own — conditionals and skipped steps legitimately
      // produce none. That inference needs at least one event to stand on:
      // with none, we know nothing about any step, and a green check beside an
      // empty Result block is a success claim we cannot back up.
      if (!eventsUnavailable) statuses.set(step.number, 'done');
      return;
    }
    if (isRunAwaitingGuidance) {
      if (index < (awaitingGuidanceIndex as number)) {
        statuses.set(step.number, 'done');
      } else if (index === awaitingGuidanceIndex) {
        statuses.set(step.number, 'paused');
      } else {
        statuses.set(step.number, 'pending');
      }
      return;
    }
    if (index < completedCount) {
      statuses.set(step.number, 'done');
    } else if (index === completedCount) {
      statuses.set(step.number, 'running');
    } else {
      statuses.set(step.number, 'pending');
    }
  });

  return statuses;
}

/**
 * The step a run is live on: executing, waiting, or stopped on an error. All
 * three mean the run has not moved past that step, so all three are where a
 * reader should be looking. `undefined` once the run is over.
 */
export function findActiveStep(
  statuses: ReadonlyMap<number, TStepRunStatus> | undefined,
): number | undefined {
  for (const [step, status] of statuses ?? []) {
    if (status === 'running' || status === 'paused' || status === 'failed') {
      return step;
    }
  }
  return undefined;
}
